import { type FC, useMemo } from "react";
import CreditCardSection from "../components/CreditCardSection/CreditCardSection";
import BillingSection from "../components/BillingSection/BillingSection";
import OrderSummary from "../components/OrderSummary/OrderSummary";
import InputField from "components/forms/InputField/InputField";
import { withUSFormattedNumberInput } from "../../../../../components/forms/USFormattedNumberInput/USFormattedNumberInput";
import { FormSectionHeader } from "components/forms";
import { Form } from "semantic-ui-react";
import { useFeatureFlag } from "../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../featureFlags";
import { type State } from "../state/slices/statesSlice";
import { type PaymentFormData, type PurchasePrice } from "../types";
import { type FormikProps } from "formik";
import { getCardProviderName } from "../utils/creditCardValidation";

import styles from "../PaymentModal.module.scss";

const USFormattedInputField = withUSFormattedNumberInput(InputField);

interface PaymentDetailsStepProps {
  formik: FormikProps<PaymentFormData>;
  purchasePrice: PurchasePrice;
  states: State[];
}
const PaymentDetailsStep: FC<PaymentDetailsStepProps> = ({ states, formik, purchasePrice }) => {
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);

  const { values } = formik;

  const stateOptions = useMemo(
    () =>
      states.map((state: { abbreviation: string; name: string }) => ({
        value: state.abbreviation,
        text: `${state.name} (${state.abbreviation})`,
      })),
    [states],
  );

  const changeCardNumber = (cardNumber: string) => {
    const cardProviderName = getCardProviderName(cardNumber);
    formik.setFieldValue("creditCardInfo.cardProviderType", cardProviderName);
  };

  return (
    <div className={styles.container}>
      <Form className={styles.content}>
        {!additionalPurchaseEnabled && (
          <section className={styles.section}>
            <FormSectionHeader title="Licenses" />
            <USFormattedInputField
              {...formik}
              propertyName={`packs[0].licensesCount`}
              label="Number of Users"
              placeholder="Enter number of users"
              value={values.packs[0].licensesCount || null}
              markAsRequired
              errorPosition={"bottom"}
              onValueChanged={(data: { value: number | null }) =>
                formik.setFieldValue(`packs[0].licensesCount`, data.value)
              }
            />
          </section>
        )}

        <section className={styles.section}>
          <FormSectionHeader title="Credit Card Information" className={styles.title} />
          <CreditCardSection
            creditCardInfo={values.creditCardInfo}
            onCardNumberChange={changeCardNumber}
            formik={formik}
          />
        </section>

        <FormSectionHeader title={"Billing Information"} />
        <BillingSection billingInfo={values.billingInfo} formik={formik} stateOptions={stateOptions} />
      </Form>

      <div className={styles.summary}>
        <OrderSummary packs={values.packs} formik={formik} purchasePrice={purchasePrice} />
      </div>
    </div>
  );
};

export default PaymentDetailsStep;
